import { Node } from 'slate';
import { flatten } from 'flat';

/**
 * Richtext-Felder werden als serialisiertes Slate-JSON in VARCHAR-Spalten persistiert.
 * Das wirksame Limit bemisst sich an der serialisierten Länge, die durch Absätze und
 * Formatierung deutlich größer ist als der sichtbare Text (siehe ADR-0004, UCPWO-396).
 */
export const richtextTooLongMessage =
    'Text zu lang zum Speichern — Formatierung zählt mit, bitte kürzen.';

export const serializedLength = (value: unknown) =>
    value == null ? 0 : JSON.stringify(value).length;

/** Auslastung des Speicherbudgets in Prozent (Basis: serialisierte JSON-Länge). */
export const serializedBudgetPercent = (value: unknown, maxLength: number) =>
    Math.round((serializedLength(value) / maxLength) * 100);

export const withMaxSerializedLength = (schema: any, maxLength: number) =>
    schema.test(
        'maxSerializedLength',
        richtextTooLongMessage,
        (value: any) => serializedLength(value) <= maxLength
    );

export function parseJsonRecursively(input: unknown, maxDepth = 4): unknown {
    let parsed = input;

    for (let i = 0; i < maxDepth; i++) {
        if (typeof parsed !== 'string' || !isJsonString(parsed)) {
            break;
        }
        parsed = JSON.parse(parsed);
    }

    return parsed;
}

export function parseSlateNodes(input: unknown, maxDepth = 4): any[] | null {
    const parsed = parseJsonRecursively(input, maxDepth);
    return Array.isArray(parsed) ? parsed : null;
}

export function isJsonString(str: any) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function safelyParseJson(str: any) {
    return parseJsonRecursively(str, 1);
}

export function serialize(nodes = []) {
    return nodes?.map?.((n) => Node.string(n)).join('\n');
}

export function replaceHtml(str: string) {
    return str
        ?.replace(/<br>/g, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ');
}

export function richtextToString(richtext: string) {
    const parsed = parseJsonRecursively(richtext);

    if (!parsed) {
        return null;
    }

    if (typeof parsed === 'string') {
        return parsed;
    }

    if (typeof parsed !== 'object') {
        return null;
    }

    return Object?.entries?.(flatten?.(parsed) || {})
        .reduce((acc: any[], [k, v]: any) => (k.includes('text') ? [...acc, v ? v : '\n'] : acc), [])
        .join('\n');
}
